import { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Grid,
    Breadcrumbs,
    Typography,
    Button,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Table,
    Paper,
    TableBody,
    IconButton,
    Switch,
    Stack,
} from '@mui/material';
import { Add, Visibility } from '@mui/icons-material';
import HomeIcon from '@mui/icons-material/Home';
import LinkNavegacao from '../../../common/Link';
import PageTitle from '../../../common/PageTitle';
import { BriefCaseIcon, EditIcon, TrashIcon } from '../../../common/Icons';
import { checkPermissionsAndRedirect, hasPermission } from '../../../../configs/functions';
import { LoadingTableRow } from '../../../common/LoadingTableRow';
import { EmptyTableRow } from '../../../common/EmptyTableRow';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import { useAppContext } from '../../../../contexts/AppContext';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { CustomTablePagination } from '../../../common/CustomTablePagination';
import { listaEmpresas } from '../companies/configs/functions';
import { SelectEmpresaFilter } from '../../../common/SelectEmpresaFilter';
import { atualizaFuncao, deletaFuncao, listaFuncoes } from './configs/functions';
import { FunctionForm } from './components/FunctionForm';
import { CustomToolbarExportTable } from '../../../common/CustomToolbarExportTable';
import { useModuleContext } from '../../../../contexts/ModuleProvider';
import {useQuery} from "@tanstack/react-query";
import {useSearchParams} from "react-router-dom";
import {listaDepartamentos} from "../organization/configs/functions";
import {FiltersButton} from "../../../common/FiltersButton";
import {ItemsTableFilter} from "../organization/components/ItemsTableFilter";

const FunctionRow = ({ data, actionsAllowed, handleExibirAlerta, handleOpenEditForm, handleUpdateStatus, updateData }) => {
    const [active, setActive] = useState(data.status);
    const [openDialog, setOpenDialog] = useState(false);

    async function handleToggleStatus() {
        if (!hasPermission(["admin", "admin_funcoes", "update_funcoes"])) {
            handleExibirAlerta('Erro', 'Você não tem permissão para executar esta ação.', 'error')
            return false;
        }

        try {
            const res = await atualizaFuncao(data.id, { status: !active })
            if (res) {
                const message = active ? 'Função inativada com sucesso' : 'Função ativada com sucesso'
                handleExibirAlerta('Sucesso', message, 'success')
                setActive(!active)
                handleUpdateStatus(data.id, !data.status)
            }
        } catch (error) {
            handleExibirAlerta('Erro', 'Erro ao alterar status', 'error')
            console.log(error)
        }
    }

    const deleteFunction = () => {
        const id = data.id;
        deletaFuncao(id)
            .then((response) => {
                const message = response.data.message ?? 'Função excluída com sucesso'
                handleExibirAlerta('Sucesso', message, 'success');
                handleUpdateStatus(id, false);
                setActive(false);
                updateData();
            }).catch((error) => {
                const message = error.response.data.message ?? 'Erro ao executar operação'
                handleExibirAlerta('Erro', message, 'error');
            })

        setOpenDialog(false)
    }

    return (
        <>
            <TableRow key={data.id}>
                <TableCell>{data.nome}</TableCell>
                <TableCell>{data.codExterno}</TableCell>
                <TableCell>{data.descricao}</TableCell>
                <TableCell>{data.observacao}</TableCell>

                <TableCell>
                    <Switch
                        checked={active}
                        onChange={handleToggleStatus}
                        disabled={!hasPermission(["admin", "admin_funcoes", "update_funcoes"]) || !actionsAllowed}
                    />
                </TableCell>

                <TableCell align='right' sx={{ minWidth: 110 }}>
                    {hasPermission(["admin", "admin_funcoes", "update_funcoes"]) &&
                        <IconButton
                            aria-label="Editar"
                            onClick={() => handleOpenEditForm(data)}
                        >
                            {actionsAllowed ? <EditIcon /> : <Visibility color='primary' />}
                        </IconButton>
                    }

                    {hasPermission(["admin", "admin_funcoes", "delete_funcoes"]) && actionsAllowed &&
                        <IconButton
                            aria-label="Excluir"
                            onClick={() => setOpenDialog(true)}
                        >
                            <TrashIcon />
                        </IconButton>
                    }
                </TableCell>
            </TableRow>

            <ConfirmDialog
                description={'Tem certeza que deseja excluir este Função?'}
                title={`Excluir Função ${data.nome}`}
                goAction={deleteFunction}
                handleClose={setOpenDialog}
                state={openDialog}
            />
        </>
    )
}

export function ListOfFunctions() {
    const { exibirAlerta } = useCommonItems();
    const { getEmpresaIdSession, setEmpresaIdSession } = useAppContext();
    const { verifyIntegrationMode, getEmpresasFilteredByModule } = useModuleContext();

    const [moduleActionsAllowed, setModuleActionsAllowed] = useState(null);

    // estados para controle de paginacao
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [numTotalItems, setNumTotalItems] = useState(0);

    const [funcoes, setFuncoes] = useState([]);
    const [empresas, setEmpresas] = useState([]);
    const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);
    const [openForm, setOpenForm] = useState(false);
    const [selectedFunction, setSelectedFunction] = useState(null);

    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState(0);

    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamPage = searchParams.get('page');
    const searchParamPerPage = searchParams.get('perPage');

    const columns = useMemo(() => [
        { field: 'nome', headerName: 'Função' },
        { field: 'codExterno', headerName: 'Cód. Externo' },
        { field: 'descricao', headerName: 'Descrição' },
        { field: 'observacao', headerName: 'Observação' },
        { field: 'status', headerName: 'Status', valueGetter: (params) => params.row.status === 1 ? 'Ativo' : 'Inativo' },
    ], []);

    const { data: empresasData, error: listaEmpresasError } = useQuery({
        queryKey: ['listaEmpresas'],
        queryFn: async () => {
            const response = await listaEmpresas(null, null, null, true);
            const empresasData = response.data.data;

            const selectedEmpresaId = getEmpresaIdSession() || empresasData[0]?.id;

            setEmpresaIdSession(selectedEmpresaId);

            setSearchParams((state) => {
                state.set('id_empresa', String(selectedEmpresaId));
                state.set('page', String(page + 1));
                state.set('perPage', String(rowsPerPage));

                return state;
            });
            return response;
        },
        refetchOnWindowFocus: false,
    });

    const queryFilters = {
        id_empresas: Array.isArray(searchParams.get('id_empresa')) ? searchParams.get('id_empresa') : [searchParams.get('id_empresa')],
        nome: searchParams.get('nome'),
        cod_externo: searchParams.get('cod_externo'),

        limit: rowsPerPage,
        offset: page * rowsPerPage,
    };

    const queryKeyListDeFuncoes = ['listOfFunctions', queryFilters];
    const { data: funcoesData, isLoading, error: funcoesError, refetch } = useQuery({
        queryKey: queryKeyListDeFuncoes,
        queryFn: () => listaFuncoes(queryFilters),
        enabled: !!searchParamPage && !!searchParamPerPage,
    });


    function handleEditFunction(item) {
        setSelectedFunction(item);
        setOpenForm(true);
    }

    const updateStatus = (categoryId, status) => setFuncoes(
        funcoes.map((item) => {
            if (item.id === categoryId) {
                item.status = status;
            }
            return item;
        })
    );

    useEffect(() => {
        if (listaEmpresasError) {
            exibirAlerta('Erro', 'Erro ao carregar empresas', 'error');
        }

        if (funcoesError) {
            exibirAlerta('Erro', 'Erro ao carregar funções', 'error');
        }
    }, [exibirAlerta, listaEmpresasError, funcoesError])


    useEffect(() => {
        if (funcoesData) {
            setFuncoes(funcoesData.data.data);
            setNumTotalItems(funcoesData.data.numero_total);
            setSelectedEmpresaId(searchParams.get('id_empresa'));
            setEmpresas(empresasData.data.data)
        }
    }, [funcoesData]);

    useEffect(() => {
        if (selectedEmpresaId) {
            const empresaHasActionsAllowed = verifyIntegrationMode('Funcao', selectedEmpresaId);
            setModuleActionsAllowed(empresaHasActionsAllowed);
        }
    }, [selectedEmpresaId, verifyIntegrationMode]);

    useEffect(() => {
        if (openForm) return;
        setSelectedFunction(null);

        const queryFilters = { ...Object.fromEntries(searchParams) }

        const tableFilters = { ...queryFilters };
        delete tableFilters.page;
        delete tableFilters.perPage;

        const activeFilters = Object.keys(tableFilters).length;

        setActiveFilters(activeFilters);
        setIsOpenFilters(activeFilters > 0);

    }, [openForm, searchParams]);

    useEffect(() => {
        const requiredPermissionsView = ["admin", "admin_departamentos", "list_departamentos"];
        checkPermissionsAndRedirect(requiredPermissionsView);
    }, []);

    return (<>
        <Helmet title="Funções" defer={false} />

        <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="stretch"
            spacing={3}
        >
            <Grid item xs={12}>
                <Breadcrumbs>
                    <LinkNavegacao to="/"><HomeIcon fontSize="small" /></LinkNavegacao>
                    <Typography variant='span'>Gerenciamento</Typography>
                    <LinkNavegacao to="/gerenciamento/pessoas">Pessoas</LinkNavegacao>
                    <Typography variant='span'>Funções</Typography>
                </Breadcrumbs>

                <PageTitle
                    icon={<BriefCaseIcon fontSize='large' />}
                    title='Funções'
                    description='Administre as funções dos colaboradores de sua(s) empresa(s).'
                />
            </Grid>

            <Grid item xs={12}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent={'space-between'}>
                    <FiltersButton
                        isOpenFilters={isOpenFilters}
                        setIsOpenFilters={setIsOpenFilters}
                        activeFilters={activeFilters}
                    />

                    {hasPermission(["admin", "admin_funcoes", "create_funcoes"]) &&
                        <Typography sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => setOpenForm(true)}
                            >
                                Cadastrar nova Função
                            </Button>
                        </Typography>
                    }
                </Stack>
            </Grid>

            <ItemsTableFilter
                isOpenFilters={isOpenFilters}
                isLoadingList={isLoading}
                empresas={empresasData?.data?.data || []}
                item={"Função"}
            />

            <Grid item xs={12}>
                <TableContainer component={Paper}>
                    <CustomToolbarExportTable
                        rows={funcoes}
                        columns={columns}
                        reportName="relatorio_funcoes"
                        reportPdfTitle="RELATÓRIO DE FUNÇÕES"
                        getDataWithoutPaginate={() => listaFuncoes({ id_empresas: [selectedEmpresaId] })}
                        getSelectedEmpresa={() => empresas.find(empresa => empresa.id === Number(selectedEmpresaId))}
                    />

                    <Table aria-label="Funções">
                        <TableHead>
                            <TableRow>
                                <TableCell>Função</TableCell>
                                <TableCell>Cód. Externo</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell>Observação</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? <LoadingTableRow />
                                : funcoes && funcoes?.length > 0
                                    ? funcoes.map((item) =>
                                        <FunctionRow
                                            key={item.id}
                                            data={item}
                                            handleExibirAlerta={exibirAlerta}
                                            handleOpenEditForm={handleEditFunction}
                                            handleUpdateStatus={updateStatus}
                                            updateData={refetch}
                                            actionsAllowed={moduleActionsAllowed}
                                        />
                                    )
                                    : <EmptyTableRow />
                            }
                        </TableBody>
                    </Table>

                    <CustomTablePagination
                        numTotalItems={numTotalItems}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        setPage={setPage}
                        setRowsPerPage={setRowsPerPage}
                    />
                </TableContainer>
            </Grid>
        </Grid>

        <FunctionForm
            open={openForm}
            setOpen={setOpenForm}
            selectedFunction={selectedFunction}
            empresas={getEmpresasFilteredByModule('Funcao', empresas, !selectedFunction)}
            actionsAllowed={moduleActionsAllowed}
        />
    </>);
}